import { createNextState } from '@reduxjs/toolkit';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CheckoutRequestFirestoreModel, usersCollection } from 'app/firebase/collections/users';
import type { RootState, AppThunk } from 'app/store';

/**
 * Reducers
 */

export type CheckoutRequestFirestoreStoreModel = CheckoutRequestFirestoreModel & {
  createdAtUTS: number;
  refund?: {
    createdAtUTS: number;
  };
};

interface VendorModel {
  title: string | null;
  loading: boolean;
}

export interface VendorsByRequestId {
  [productId: string]: VendorModel;
}

type CheckoutRequestsState = {
  loadingRequests?: boolean;
  detail?: CheckoutRequestFirestoreStoreModel;
  vendors: VendorsByRequestId;
};

const initialState: CheckoutRequestsState = {
  loadingRequests: undefined,
  detail: undefined,
  vendors: {},
};

const checkoutRequestsSlice = createSlice({
  name: 'checkoutRequests',
  initialState,
  reducers: {
    clean: (): CheckoutRequestsState => {
      return { ...initialState };
    },

    loadRequests: (state, action: PayloadAction<boolean>): CheckoutRequestsState => {
      return {
        ...state,
        loadingRequests: action.payload,
      };
    },

    setRequest: (
      state,
      action: PayloadAction<CheckoutRequestFirestoreStoreModel>,
    ): CheckoutRequestsState => {
      return createNextState(state, (draft) => {
        draft.detail = action.payload;
        draft.loadingRequests = false;
      });
    },

    replaceVendor: (
      state,
      action: PayloadAction<{ productId: string; loading: boolean; title: string | null }>,
    ): CheckoutRequestsState => {
      const { productId, loading, title } = action.payload;
      return {
        ...state,
        vendors: {
          ...state.vendors,
          [productId]: {
            title,
            loading,
          },
        },
      };
    },
  },
});

/**
 * Actions
 */
const { clean, loadRequests, setRequest, replaceVendor } = checkoutRequestsSlice.actions;

export const cleanCheckoutRequests = clean;

export const fetchCheckoutRequestById =
  (orderId: string, uid?: string): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const userUid = uid || state.user.documentId;

    if (userUid) {
      const response = await usersCollection.fetchCheckoutRequestByOrderId(userUid, orderId);
      if (!response) {
        dispatch(loadRequests(false));
        return;
      }
      const request = convertToStoreModel(response);
      dispatch(setRequest(request));
    }
  };

export const setVendorByProductId =
  (productId: string): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const vendor = state.checkoutRequests.vendors[productId];

    if (!vendor) {
      dispatch(replaceVendor({ productId, loading: true, title: null }));
    }
  };

export const updateVendorByProductId =
  (productId: string, title: string | null = null): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const vendor = state.checkoutRequests.vendors[productId];

    if (vendor && vendor.loading) {
      dispatch(replaceVendor({ productId, loading: false, title }));
    }
  };

/**
 * Selectors
 */
export const selectCheckoutRequest = (state: RootState) => {
  return state.checkoutRequests.detail;
};

const selectCheckoutRequestsLoadingFirstTime = (state: RootState) =>
  state.checkoutRequests.loadingRequests === undefined;
const selectVendorsLoading = (state: RootState) => {
  const { vendors } = state.checkoutRequests;
  return Object.keys(vendors).some((id) => vendors[id].loading);
};
export const selectCheckoutRequestsLoading = (state: RootState) =>
  state.checkoutRequests.loadingRequests ||
  selectCheckoutRequestsLoadingFirstTime(state) ||
  selectVendorsLoading(state);
export const selectVendors = (state: RootState) => state.checkoutRequests.vendors;

interface FirebaseRequestedAt {
  seconds: number;
  nanoseconds: number;
}

const convertFirebaseDateToUTC = ({ seconds, nanoseconds }: FirebaseRequestedAt): number => {
  return new Date(seconds * 1000 + nanoseconds / 1000000).getTime();
};

/**
 * Utils
 */
const convertToStoreModel = ({
  createdAt,
  // Skipped this intentionally, to prevent React warning about Firebase date format
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  autoCancelTask,
  refund,
  ...item
}: CheckoutRequestFirestoreModel): CheckoutRequestFirestoreStoreModel => {
  return {
    ...item,
    createdAtUTS: convertFirebaseDateToUTC(createdAt),
    createdAt: {
      seconds: createdAt.seconds,
      nanoseconds: createdAt.nanoseconds,
    },
    refund: refund && {
      createdAtUTS: refund && convertFirebaseDateToUTC(refund.createdAt),
      createdAt: {
        seconds: refund.createdAt.seconds,
        nanoseconds: refund.createdAt.nanoseconds,
      },
    },
  } as CheckoutRequestFirestoreStoreModel;
};

export default checkoutRequestsSlice.reducer;
